import type {
  Pass,
  PassDefinition,
  PassObjective,
  PassObjectiveProgressState,
  PassObjectiveResourceType,
  PassRewardKind,
  PassRewardTier,
  PassRewardTierItem
} from '../types/pass'

export const PASS_PAGE_VARIANT = 'purple' as const

const RESOURCE_TYPE_LABELS: Record<PassObjectiveResourceType, string> = {
  Mission: 'missões',
  Journey: 'jornadas',
  Level: 'desafios',
  Document: 'documentos'
}

const RESOURCE_TYPE_SINGULAR: Record<PassObjectiveResourceType, string> = {
  Mission: 'missão',
  Journey: 'jornada',
  Level: 'desafio',
  Document: 'documento'
}

const REWARD_KIND_LABELS: Record<PassRewardKind, string> = {
  coins: 'moedas',
  points: 'pontos',
  reward_item: 'item de loja',
  cosmetic: 'cosmético',
  title: 'título'
}

// Nome do TIPO da recompensa, usado na revelação do resgate — onde o aluno
// precisa saber o que recebeu, não só o nome do item.
const REWARD_KIND_TITLES: Record<PassRewardKind, string> = {
  coins: 'Moedas',
  points: 'Pontos',
  reward_item: 'Item de loja',
  cosmetic: 'Cosmético',
  title: 'Título'
}

export function passTitle(pass: Pass) {
  return pass.kind === 'campaign' && pass.campaign_name
    ? pass.campaign_name
    : 'Passe da Plataforma'
}

export function passProgress(pass: Pass) {
  return Math.min(100, Math.max(0, pass.progress))
}

// Sobrepõe o estado por-usuário dos objetivos (lista plana do /gamification/
// progress) sobre os passes vindos do /user/passes. Atualiza APENAS os campos
// do objetivo (current_count/completed/status) — o progresso a nível de passe
// (`pass.progress`) é deliberadamente intocado, pois é informação exclusiva da
// tela de passe. O footer da listagem de jornadas calcula seu progresso à parte
// (ver `journeysPassFooterView`). Objetivos sem registro (ex.: passe de
// campanha, ausente no /progress) mantêm o valor original do /user/passes.
export function applyObjectiveProgress(
  passes: Pass[],
  states: PassObjectiveProgressState[]
): Pass[] {
  if (states.length === 0) return passes

  const stateByObjectiveId = new Map(
    states.map(state => [state.pass_objective_id, state])
  )

  return passes.map(pass => ({
    ...pass,
    pass_objectives: pass.pass_objectives.map(objective => {
      const state = stateByObjectiveId.get(objective.id)
      if (!state) return objective

      return {
        ...objective,
        current_count: state.current_count,
        completed: state.completed,
        status: state.status
      }
    })
  }))
}

// Decide, no cliente, quais passes do /gamification/data (todos vigentes +
// futuros, globais) são válidos para exibir agora — mesma regra do backend
// `Pass.visible_to`: ativo, dentro da janela (starts_at ≤ agora ≤ ends_at,
// futuros ficam ocultos até começar) e, se for de campanha, o usuário participa.
export function validGamificationPasses(
  passes: PassDefinition[],
  campaignIds: number[],
  nowMs: number = Date.now()
): PassDefinition[] {
  return passes.filter(pass => {
    if (!pass.active) return false

    const startsMs = new Date(pass.starts_at).getTime()
    const endsMs = new Date(pass.ends_at).getTime()
    if (nowMs < startsMs || nowMs > endsMs) return false

    if (pass.kind === 'campaign') {
      return pass.campaign_id != null && campaignIds.includes(pass.campaign_id)
    }
    return true
  })
}

// View pronta para o footer do passe na listagem de jornadas, construída do
// zero e apenas a partir do gamification: definições (`Data.passes`) + estados
// por objetivo e campanhas (`/gamification/progress`). Sem /user/passes, sem
// `pass.progress`, sem montar um Pass intermediário.
//
// `progress` = fração de objetivos CONCLUÍDOS (`state.completed`) sobre o total
// de objetivos do passe — mesma regra do backend `Pass#progress_for`. Retorna
// null quando não há passe válido a exibir.
export interface JourneysPassFooterView {
  title: string
  progress: number
  nextReward: string | null
}

export function journeysPassFooterView(
  passes: PassDefinition[],
  campaignIds: number[],
  states: PassObjectiveProgressState[],
  nowMs: number = Date.now()
): JourneysPassFooterView | null {
  const valid = validGamificationPasses(passes, campaignIds, nowMs)
  // Um passe por vez: prioriza o da plataforma, senão o primeiro válido.
  const pass = valid.find(candidate => candidate.kind === 'default') ?? valid[0]
  if (!pass) return null

  const objectives = pass.pass_objectives ?? []
  const total = objectives.length
  const completedObjectiveIds = new Set(
    states.filter(state => state.completed).map(state => state.pass_objective_id)
  )
  const completed = objectives.filter(objective =>
    completedObjectiveIds.has(objective.id)
  ).length
  const progress = total === 0 ? 0 : Math.floor((completed / total) * 100)

  const nextTier = [...(pass.pass_reward_tiers ?? [])]
    .sort((a, b) => a.percent_threshold - b.percent_threshold)
    .find(tier => progress < tier.percent_threshold)
  const nextReward = nextTier
    ? (nextTier.pass_reward_tier_items ?? []).map(rewardLabel).join(' + ') ||
      'Recompensa especial'
    : null

  const title =
    pass.kind === 'campaign' && pass.campaign_name
      ? pass.campaign_name
      : 'Passe da Plataforma'

  return { title, progress, nextReward }
}

export function completedObjectivesCount(pass: Pass) {
  return pass.pass_objectives.filter(objective => objective.completed).length
}

function totalObjectivesCount(pass: Pass) {
  return pass.pass_objectives.length
}

export function objectiveCountLabel(count: number) {
  return `${count} ${count === 1 ? 'objetivo' : 'objetivos'}`
}

// Accepts a structural subset so both progress-shaped PassObjective and the
// definition-only PassObjectiveDefinition (no `completed` field) can reuse
// this label — see webapp-missions-category-filters design.md Decision 6.
type ObjectiveLabelInput = Pick<PassObjective, 'mode' | 'resource_type' | 'quantity'>

export function objectiveLabel(objective: ObjectiveLabelInput) {
  if (objective.mode === 'specific') {
    return `Acessar ${RESOURCE_TYPE_SINGULAR[objective.resource_type] ?? 'conteúdo'} específico`
  }
  const noun = RESOURCE_TYPE_LABELS[objective.resource_type] ?? 'itens'
  return `Concluir ${objective.quantity} ${noun}`
}

export function rewardLabel(item: PassRewardTierItem) {
  if (item.kind === 'coins' || item.kind === 'points') {
    return `${item.amount ?? 0} ${REWARD_KIND_LABELS[item.kind]}`
  }
  return item.target_name ?? REWARD_KIND_LABELS[item.kind]
}

export function rewardTypeLabel(item: PassRewardTierItem) {
  return REWARD_KIND_TITLES[item.kind] ?? 'Recompensa'
}

// "Cosmético: Ana da Vila", "Pontos: 500". Diferente de `rewardLabel`, que
// devolve só o valor/nome e continua servindo o resumo do tier e o footer de
// jornadas — aqui o tipo é parte da mensagem.
export function rewardDetailLabel(item: PassRewardTierItem) {
  const type = rewardTypeLabel(item)

  if (item.kind === 'coins' || item.kind === 'points') {
    return `${type}: ${item.amount ?? 0}`
  }

  return item.target_name ? `${type}: ${item.target_name}` : type
}

// O objetivo entrega moedas (`coin_reward`), não uma lista de itens como o
// tier. Convertê-lo ao formato de item deixa a revelação do resgate ser a
// mesma nos dois pontos da tela do passe.
export function objectiveRewardItems(
  objective: Pick<PassObjective, 'id' | 'coin_reward'>
): PassRewardTierItem[] {
  if (objective.coin_reward <= 0) return []

  return [
    {
      id: objective.id,
      kind: 'coins',
      amount: objective.coin_reward,
      target_id: null,
      target_name: null,
      target_image_url: null
    }
  ]
}

export function tierRewardSummary(tier: PassRewardTier) {
  return tier.pass_reward_tier_items.map(rewardLabel).join(' + ') || 'Recompensa especial'
}

export type TierState = 'delivered' | 'available' | 'locked'

// O backend é a fonte da verdade do status do tier (blocked/available/delivered).
// 'blocked' é mapeado para 'locked' (vocabulário da UI).
export function tierState(tier: PassRewardTier): TierState {
  if (tier.status === 'delivered') return 'delivered'
  if (tier.status === 'available') return 'available'
  return 'locked'
}

export function sortedTiers(pass: Pass): PassRewardTier[] {
  return [...pass.pass_reward_tiers].sort(
    (a, b) => a.percent_threshold - b.percent_threshold
  )
}

// Colapsa objetivos escalonados do mesmo recurso (ex.: concluir 1, 5, 10 missões)
// numa ÚNICA linha, exibindo um objetivo por vez. Regra por grupo:
//   - grupo = objetivos `quantity` de um mesmo `resource_type` (objetivos
//     `specific`, de recurso pontual, nunca colapsam — cada um é seu próprio grupo);
//   - representante = o de MENOR `quantity` ainda NÃO resgatado (`status !== 'delivered'`);
//   - se todos já foram resgatados, o de MAIOR `quantity` (estado final "Resgatado").
// A ordem dos grupos preserva a primeira aparição no payload do backend. Ao
// resgatar o representante, o refetch avança automaticamente para o próximo.
export function collapsedObjectives(pass: Pass): PassObjective[] {
  const groupOrder: string[] = []
  const groups = new Map<string, PassObjective[]>()

  for (const objective of pass.pass_objectives) {
    const key =
      objective.mode === 'quantity'
        ? `q:${objective.resource_type}`
        : `s:${objective.id}`
    const group = groups.get(key)
    if (group) {
      group.push(objective)
    } else {
      groups.set(key, [objective])
      groupOrder.push(key)
    }
  }

  return groupOrder.map(key => {
    const group = [...(groups.get(key) ?? [])].sort(
      (a, b) => a.quantity - b.quantity || a.id - b.id
    )
    return (
      group.find(objective => objective.status !== 'delivered') ??
      group[group.length - 1]
    )
  })
}

export function objectivesRemainingForTier(pass: Pass, tier: PassRewardTier) {
  const totalObjectives = totalObjectivesCount(pass)

  if (totalObjectives === 0) return 0

  const objectivesRequired = Math.ceil(
    (tier.percent_threshold / 100) * totalObjectives
  )

  return Math.max(0, objectivesRequired - completedObjectivesCount(pass))
}

// Próximo marco a atingir: primeiro tier ainda não alcançado. Compara com
// `pass.progress` (usado pela tela de passe).
export function nextMilestone(
  pass: Pass,
  progress: number = pass.progress
): PassRewardTier | undefined {
  return sortedTiers(pass).find(tier => progress < tier.percent_threshold)
}

export function remainingObjectivesForNextMilestone(pass: Pass) {
  const milestone = nextMilestone(pass)

  if (!milestone) return 0

  return objectivesRemainingForTier(pass, milestone)
}

// Recompensa em destaque: um tier alcançado mas ainda não entregue (a "resgatar");
// senão o próximo marco bloqueado como prévia.
export function highlightedTier(pass: Pass): PassRewardTier | undefined {
  const tiers = sortedTiers(pass)
  return (
    tiers.find(tier => tierState(tier) === 'available') ??
    nextMilestone(pass)
  )
}
